// File: src/components/Shifts/ShiftApprovalPanel.jsx

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchEmergencyStatus, isFeatureLocked } from '@/utils/emergencyLock';
import { useNavigate } from 'react-router-dom';
import { useUserPermissions } from '@/hooks/useUserPermissions';

const ShiftApprovalPanel = ({ filters = {} }) => {
  const [shifts, setShifts] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const { canApproveShifts, user } = useUserPermissions();
  const navigate = useNavigate();

  useEffect(() => {
    if (!canApproveShifts) {
      navigate('/unauthorized');
    }
  }, [canApproveShifts, navigate]);

  useEffect(() => {
    const fetchShifts = async () => {
      setLoading(true);
      try {
        const res = await axios.get('/solo-mdsh-api/staffing/shift-approval', {
          params: filters,
        });
        setShifts(res.data || []);
        setMessage('');
      } catch (err) {
        console.error('Error fetching shifts:', err);
        setMessage('Unable to fetch pending shifts.');
      } finally {
        setLoading(false);
      }
    };

    const checkEmergency = async () => {
      const mode = await fetchEmergencyStatus();
      setIsLocked(isFeatureLocked(mode, ['cyberattack', 'recovery']));
    };

    fetchShifts();
    checkEmergency();
  }, [filters]);

  const handleAction = async (shiftId, decision) => {
    try {
      const res = await axios.post('/solo-mdsh-api/staffing/shift-approval', {
        shift_id: shiftId,
        status: decision,
        approver_id: user?.staff_id,
      });

      setMessage(res.data.message || 'Action completed.');
      setShifts((prev) => prev.filter((s) => s.shift_id !== shiftId));
    } catch (err) {
      console.error('Approval error:', err);
      setMessage('Approval failed.');
    }
  };

  return (
    <div className="bg-neutral-800 p-6 rounded-2xl shadow-xl text-white">
      <h2 className="text-xl font-semibold mb-4">✅ Pending Shift Approval</h2>

      {isLocked ? (
        <div className="bg-red-600 text-white p-3 rounded-lg font-medium text-center">
          🚫 Approvals disabled due to Emergency Mode.
        </div>
      ) : loading ? (
        <p className="text-blue-300">Loading pending shifts...</p>
      ) : shifts.length === 0 ? (
        <p className="text-gray-400">No pending shifts to review.</p>
      ) : (
        <table className="w-full text-sm table-auto border-separate border-spacing-y-2">
          <thead>
            <tr className="text-left border-b border-gray-600">
              <th className="py-2">Staff</th>
              <th>Date</th>
              <th>Time</th>
              <th>Location</th>
              <th>Hours</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {shifts.map((shift) => (
              <tr key={shift.shift_id} className="bg-[#1f2937] hover:bg-[#2d3748]">
                <td className="py-2 px-2">{shift.staff_name}</td>
                <td>{shift.date}</td>
                <td>{shift.start_time} - {shift.end_time}</td>
                <td>{shift.location}</td>
                <td>{shift.total_hours}</td>
                <td className="flex gap-2 py-1">
                  <button
                    className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded"
                    onClick={() => handleAction(shift.shift_id, 'approved')}
                  >
                    Approve
                  </button>
                  <button
                    className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded"
                    onClick={() => handleAction(shift.shift_id, 'rejected')}
                  >
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {message && (
        <div className="mt-4 text-yellow-400 font-medium">{message}</div>
      )}
    </div>
  );
};

export default ShiftApprovalPanel;
